/* eslint-disable sonarjs/cognitive-complexity */
import type { CMSError } from '@/lib/cms/types/CMSError'
import { getGridPageData } from '@/lib/services/Products/getGridPageData'
import useGlobalStore from '@/store/useGlobalStore'
import { useEffect, useState, useCallback, useRef } from 'react'
import debounce from 'lodash.debounce'
import type { CosmicProductType } from '@/lib/cms/cosmic/types/CosmicProductType'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'
import { isAvailableForCustomerType } from '@/lib/utils/user/isAvailableForCustomerType'

type Props = {
  products?: CosmicProductType[] | { error: CMSError } | null
}

export const useSearchProducts = ({ products }: Props) => {
  const {
    app: { isSearchBarActive, searchBarValue, locale, alpha2 },
    user: { customerType },
  } = useGlobalStore()

  const [isLoading, setIsLoading] = useState(false)
  const [allProducts, setAllProducts] = useState<CosmicProductType[] | null>(
    null,
  )

  const [filteredProducts, setFilteredProducts] = useState<
    CosmicProductType[] | null
  >(null)

  const hasTrackedSearch = useRef<string | null>(null)

  useEffect(() => {
    if (!isSearchBarActive) return
    ;(async () => {
      if (!isSearchBarActive) return
      if (isSearchBarActive && allProducts) return

      if (!products) {
        setIsLoading(true)

        const fetchedProducts = await getGridPageData({
          locale,
          alpha2,
          category: '/',
        })

        setTimeout(() => {
          // This removes an annoying flicker when the skeleton is shown for a very short time
          setIsLoading(false)
        }, 500)

        if (
          !fetchedProducts ||
          !fetchedProducts.products ||
          'error' in fetchedProducts.products
        ) {
          return
        }

        const validProducts = fetchedProducts.products.filter(
          (product): product is CosmicProductType => product !== null,
        )

        setAllProducts(validProducts)
      } else {
        if ('error' in products) return

        setAllProducts(products)
      }
    })()
  }, [alpha2, locale, products, isSearchBarActive])

  const handleDebouncedFilter = useCallback(
    // TODO: Fix
    // eslint-disable-next-line react-compiler/react-compiler
    debounce((searchValue: string, all_products: CosmicProductType[]) => {
      const searchValues = searchValue.toLowerCase().split(' ')

      const filtered = all_products.filter((product) => {
        const { name } = product

        const allProductSkus = [
          ...new Set(
            product.variations
              .map((variation) => variation.sku.toLowerCase())
              .filter((sku): sku is string => !!sku),
          ),
        ]

        const allProductFlavors = [
          ...new Set(
            product.attributes
              .flatMap((attribute) =>
                attribute.options.map((option) => option.name?.toLowerCase()),
              )
              .filter((flavor): flavor is string => !!flavor),
          ),
        ]

        const searchableFields = [
          name.toLowerCase(),
          ...allProductSkus,
          ...allProductFlavors,
        ]

        return searchValues.every((value) =>
          searchableFields.some((field) => field.includes(value)),
        )
      })

      const rankedFiltered = filtered.sort((a, b) => {
        const titleMatchA = searchValues.some((value) =>
          a.name.toLowerCase().includes(value),
        )
        const titleMatchB = searchValues.some((value) =>
          b.name.toLowerCase().includes(value),
        )

        if (titleMatchA && !titleMatchB) return -1
        if (!titleMatchA && titleMatchB) return 1

        const matchCount = (product: CosmicProductType) =>
          searchValues.filter((value) =>
            product.name.toLowerCase().includes(value),
          ).length

        return matchCount(b) - matchCount(a)
      })

      setFilteredProducts(rankedFiltered)

      // Track search results with customer type filtering
      if (
        searchValue.trim().length > 0 &&
        hasTrackedSearch.current !== searchValue
      ) {
        // Apply the same filtering logic as ProductsGrid
        const availableProductsCount = rankedFiltered.filter((product) =>
          isAvailableForCustomerType({
            customerType,
            availableFor: product.availableFor,
          }),
        ).length

        if (availableProductsCount > 0) {
          mixpanelService
            .trackEvent(MixpanelEvent.SEARCH_RESULTS_FOUND, {
              search_term: searchValue,
              results_count: availableProductsCount,
            })
            .catch(() => {})
        } else {
          mixpanelService
            .trackEvent(MixpanelEvent.SEARCH_NO_RESULTS_FOUND, {
              search_term: searchValue,
            })
            .catch(() => {})
        }
        hasTrackedSearch.current = searchValue
      }
    }, 500),
    [],
  )

  useEffect(() => {
    if (!isSearchBarActive || !allProducts || searchBarValue.length <= 2) {
      setFilteredProducts(null)
      return
    }

    handleDebouncedFilter(searchBarValue, allProducts)
  }, [allProducts, isSearchBarActive, searchBarValue, handleDebouncedFilter])

  return { isLoading, filteredProducts }
}
